import {
  ArtifactsListInfo,
  CommentEntry,
  CommentThreadEntry,
  Document,
  DocumentTemplate as DBDocumentTemplate,
  DocumentType as DBDocumentType,
  Prisma,
  Project,
  ProjectGroup,
  ProjectTemplate as DBProjectTemplate,
  UsageTierEntry,
  User as DBUser,
  UserCompanyInviteEntry,
  UserInfoEntity,
  UserUsage as DBUserUsage,
} from "@prisma/client";
import {
  Feature,
  FeatureDetails,
  ProjectPlan,
  ProjectPlanDetails,
  Task,
  TaskDetails
} from "@/pb/api/devplan/types/tasks";
import {
  DocumentEntity,
  DocumentType,
  documentTypeFromJSON,
  documentTypeToJSON,
  ProjectDetails,
  ProjectEntity,
  ProjectStatus
} from "@/pb/api/devplan/types/documents";
import {
  CodingAssistantTemplate,
  CustomDocumentTemplate,
  DocumentTemplate,
  FeatureTemplate,
  PlanSubItemTemplate,
  PrdTemplate,
  ProjectPlanTemplate,
  ProjectTemplate,
  ProjectTemplateDetails,
  TemplateMeta,
  TemplateStatus
} from "@/pb/api/devplan/types/templates";
import {User, UserCompanyInvite} from "@/pb/api/devplan/types/company";
import {UsageTier, UserUsageDetails} from "@/pb/api/devplan/types/usage";
import {ArtifactsList} from "@/pb/api/devplan/types/artifacts";
import {Comment, CommentThread} from "@/pb/api/devplan/types/comments";
import {Group} from "@/pb/api/devplan/types/grouping";
import {UserInfo} from "@/pb/api/devplan/types/user";
import DocumentTemplateUncheckedCreateInput = Prisma.DocumentTemplateUncheckedCreateInput;

export interface DocumentFeatures {
  features: Feature[]
  tasks: Record<string, Task[]>
}

export function fromDocumentType(t: DocumentType): DBDocumentType {
  return documentTypeToJSON(t) as DBDocumentType
}

export function documentToEntity(d: Document, p: Project | ProjectEntity): DocumentEntity {
  const pe = typeof p.details === 'string' ? projectToEntity(p as Project) : p as ProjectEntity;
  return DocumentEntity.create({
    ...d,
    type: documentTypeFromJSON(d.type),
    content: d.content || undefined,
    details: d.details || undefined,
    parentId: d.parentId || undefined,
    description: d.description || undefined,
    initialInput: d.initialInput || undefined,
    createdBy: d.createdBy || undefined,
    isDraftPart: !pe.details?.status || pe.details.status === ProjectStatus.DRAFT,
    numericId: d.numericId ?? undefined,
    slug: d.slug ?? undefined,
  })
}

type DocumentTemplateType = NonNullable<DocumentTemplate["type"]>;

export function toDocumentTemplate(t: DBDocumentTemplate): DocumentTemplate {
  const {id, createdAt, updatedAt, title, description} = t
  const meta = t.meta ? TemplateMeta.fromJSON(JSON.parse(t.meta)) : TemplateMeta.create({})
  return DocumentTemplate.create({
    id, createdAt, updatedAt,
    meta: {...meta, ...{name: title, description, status: t.status as TemplateStatus}},
    type: getSpecificDocumentTemplate(t),
  })
}

function getSpecificDocumentTemplate(t: DBDocumentTemplate): DocumentTemplateType {
  const js = JSON.parse(t.template)
  switch (t.type) {
    case DBDocumentType.PRD:
      return {$case: "prd", value: PrdTemplate.fromJSON(js)}
    case DBDocumentType.PROJECT_PLAN:
      return {$case: "projectPlan", value: ProjectPlanTemplate.fromJSON(js)}
    case DBDocumentType.FEATURE:
      return {$case: "feature", value: FeatureTemplate.fromJSON(js)}
    case DBDocumentType.TASK:
      return {$case: "planSubItem", value: PlanSubItemTemplate.fromJSON(js)}
    case DBDocumentType.CUSTOM_DOCUMENT:
      return {$case: "customDocument", value: CustomDocumentTemplate.fromJSON(js)}
    case DBDocumentType.CODING_ASSISTANT:
      return {$case: "codingAssistant", value: CodingAssistantTemplate.fromJSON(js)}
    default:
      throw new Error(`Unknown document type: ${t.type}`)
  }
}

export function toOptionalProjectTemplate(t: DBProjectTemplate | undefined | null): ProjectTemplate | undefined {
  return t ? toProjectTemplate(t) : undefined
}

export function toProjectTemplate(t: DBProjectTemplate): ProjectTemplate {
  const {id, createdAt, updatedAt, title, description, template} = t
  return ProjectTemplate.create({
    id, createdAt, updatedAt,
    meta: TemplateMeta.create({name: title, description, status: t.status as TemplateStatus,}),
    details: ProjectTemplateDetails.fromJSON(JSON.parse(template)),
  })
}

export function optionalProjectToEntity(p: Project | null): ProjectEntity | undefined {
  return p ? projectToEntity(p) : undefined;
}

export function projectToEntity(p: Project): ProjectEntity {
  return ProjectEntity.create({
    ...p,
    createdBy: p.createdBy || undefined,
    details: getProjectDetailsFromJson(p.details),
    numericId: p.numericId ?? undefined,
    slug: p.slug ?? undefined,
    kind: p.kind ?? undefined,
    isArchived: p.isArchived ?? false,
  })
}

function getProjectDetailsFromJson(detailsJson: string | undefined): ProjectDetails | undefined {
  if (detailsJson === undefined) {
    return undefined
  }
  return detailsJson.length > 0
    ? ProjectDetails.fromJSON(JSON.parse(detailsJson))
    : ProjectDetails.create({status: ProjectStatus.DRAFT})
}

export function documentToTask(dbTask: DocumentEntity): Task {
  return Task.create({
    id: dbTask.id,
    title: dbTask.title,
    description: dbTask.content,
    companyId: dbTask.companyId,
    parentId: dbTask.parentId,
    projectId: dbTask.projectId,
    details: dbTask.details ? TaskDetails.fromJSON(JSON.parse(dbTask.details)) : undefined,
    numericId: dbTask.numericId,
  })
}

export function documentToFeature(docEntity: DocumentEntity): Feature {
  return Feature.create({
    id: docEntity.id,
    title: docEntity.title,
    description: docEntity.content ?? "",
    companyId: docEntity.companyId,
    parentId: docEntity.parentId,
    projectId: docEntity.projectId,
    createdAt: docEntity.createdAt,
    details: docEntity.details ? FeatureDetails.fromJSON(JSON.parse(docEntity.details)) : undefined,
    numericId: docEntity.numericId,
  })
}

export function documentToProjectPlan(doc: DocumentEntity): ProjectPlan {
  return ProjectPlan.create({
    id: doc.id,
    companyId: doc.companyId,
    title: doc.title,
    description: doc.description ?? "",
    projectId: doc.projectId,
    parentId: doc.parentId,
    details: doc.details ? ProjectPlanDetails.fromJSON(JSON.parse(doc.details)) : undefined,
  })
}

export function getProjectPlan(allProjectDocs: DocumentEntity[]): ProjectPlan | undefined {
  const planDoc = allProjectDocs.find(d => d.type === DocumentType.PROJECT_PLAN)
  return planDoc ? documentToProjectPlan(planDoc) : undefined
}

export function fromDocumentTemplate(template: DocumentTemplate, id: string | undefined): DocumentTemplateUncheckedCreateInput {
  return {
    id,
    title: template.meta?.name ?? "",
    description: template.meta?.description ?? "",
    type: getTemplateDBDocumentType(template),
    template: JSON.stringify(getTemplateSerializer(template).toJSON(template.type!.value)),
    meta: template.meta ? JSON.stringify(TemplateMeta.toJSON(template.meta)) : undefined,
    status: template.meta?.status ?? TemplateStatus.ACTIVE,
  }
}

export function getTemplateDBDocumentType(template: DocumentTemplate): DBDocumentType {
  const typeCase = template.type?.$case

  // Special case handling
  if (typeCase === "planSubItem") {
    return DBDocumentType.TASK
  }

  if (!typeCase) {
    throw new Error(`Missing template type`)
  }

  // Convert camelCase to UPPER_SNAKE_CASE
  const upperSnakeCase = typeCase
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toUpperCase()

  // Try to find the corresponding DBDocumentType
  try {
    // Access the enum value using the string key
    const docType = DBDocumentType[upperSnakeCase as keyof typeof DBDocumentType]
    if (docType !== undefined) {
      return docType
    }
  } catch {
    throw new Error(`Unknown template type: ${typeCase}`)
  }
  throw new Error(`Unknown template type: ${typeCase}`)
}

interface TemplateSerializer {
  toJSON(message: unknown): unknown;
}

function getTemplateSerializer(template: DocumentTemplate): TemplateSerializer {
  const typeCase = template.type?.$case

  if (!typeCase) {
    throw new Error(`Missing template type`)
  }

  // Map template type to serializer
  switch (typeCase) {
    case "prd":
      return PrdTemplate
    case "projectPlan":
      return ProjectPlanTemplate
    case "feature":
      return FeatureTemplate
    case "planSubItem":
      return PlanSubItemTemplate
    case "customDocument":
      return CustomDocumentTemplate
    case "codingAssistant":
      return CodingAssistantTemplate
    default:
      throw new Error(`Unknown template type: ${typeCase}`)
  }
}

export function toOptionalUser(u: DBUser | null): User | undefined {
  return u ? toUser(u) : undefined
}

export function toUser(u: DBUser): User {
  return {
    id: u.id,
    email: u.email,
    name: u.name || "",
    image: u.image || "",
    trialStarted: u.trialStarted,
    forceTrial: u.forceTrial,
    loopsCustomerCreated: u.loopsCustomerCreated,
    createdAt: u.createdAt,
  }
}

export function toOptionalUserUsage(u: DBUserUsage | null): UserUsageDetails | undefined {
  return u ? toUserUsage(u) : undefined
}

export function toUserUsage(u: DBUserUsage): UserUsageDetails {
  const {userId, lastReset, createdAt, updatedAt} = u
  return {
    ...UserUsageDetails.fromJSON(JSON.parse(u.details)),
    userId, lastReset: lastReset ?? undefined, createdAt, updatedAt
  };
}

export function toOptionalArtifactList(l: ArtifactsListInfo | null): ArtifactsList | undefined {
  return l ? toArtifactList(l) : undefined
}

export function toArtifactList(l: ArtifactsListInfo): ArtifactsList {
  return {...ArtifactsList.fromJSON(JSON.parse(l.details)), ...l}
}

export function toOptionalComment(e: CommentEntry | null): Comment | undefined {
  return e ? toComment(e) : undefined
}

export function toComment(e: CommentEntry): Comment {
  const {id, createdAt, updatedAt, authorId, parentId, threadId, companyId} = e
  const dbData = {id, createdAt, updatedAt, authorId, parentId, threadId, companyId}
  const parsed = Comment.fromJSON(JSON.parse(e.details))
  return Comment.create({
    ...parsed, ...dbData, parentId: parentId || undefined,
    meta: {createdBy: authorId, createdAt, updatedAt}
  })
}

export function toCommentThread(e: CommentThreadEntry): CommentThread {
  const {id, createdAt, updatedAt, createdBy, companyId} = e
  const dbData = {id, companyId}
  const parsed = e.details ? CommentThread.fromJSON(JSON.parse(e.details)) : CommentThread.create({})
  return CommentThread.create({...parsed, ...dbData, meta: {createdBy, createdAt, updatedAt}})
}

export function toOptionalCompanyInvite(e: UserCompanyInviteEntry | undefined | null): UserCompanyInvite | undefined {
  return e ? toCompanyInvite(e) : undefined
}

export function toCompanyInvite(e: UserCompanyInviteEntry): UserCompanyInvite {
  const {id, createdAt, createdBy, role, companyId, email} = e
  return {id, createdAt, createdBy, role, companyId, email}
}

export function toTier(tier: UsageTierEntry): UsageTier {
  return {...UsageTier.fromJSON(JSON.parse(tier.details)), ...tier}
}

export function toOptionalGroup(dbGroup: ProjectGroup | undefined | null): Group | undefined {
  return dbGroup ? toGroup(dbGroup) : undefined
}

export function toGroup(dbGroup: ProjectGroup): Group {
  const {id, createdAt, updatedAt, companyId, revision} = dbGroup
  const details = Group.fromJSON(JSON.parse(dbGroup.details))
  return {...cleanChildren(details), id, createdAt, updatedAt, companyId, revision}
}

function cleanChildren(g: Group): Group {
  const seenChildren = new Set<string>([])
  const newItems = g.items.map(i => {
    const anySeen = i.childItems.some(k => seenChildren.has(k))
    const result = anySeen ? {...i, childItems: i.childItems.filter(key => !seenChildren.has(key))} : i
    i.childItems.forEach(k => seenChildren.add(k))
    return result
  })
  return {...g, items: newItems}
}

export function toOptionalUserInfo(dbInfo: UserInfoEntity | null | undefined): UserInfo | undefined {
  return dbInfo ? toUserInfo(dbInfo) : undefined
}

export function toUserInfo(dbInfo: UserInfoEntity): UserInfo {
  const {userId, details} = dbInfo
  const parsed = UserInfo.fromJSON(JSON.parse(details));
  return {...parsed, userId};
}
